import type {
  KeyMessageGroupFragment,
  KeyMessageItemFragment,
} from "@/data-access/news-feeds/fragments/key-message-fragments";
import type { FilterExclusion } from "@/types/shared";
import type { ResultOf } from "gql.tada";
import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import { createKeyMessageActions } from "./key-message.actions";

/** When true, count is being recalculated (e.g. after filter change); cleared when subscription delivers updated total. */
export interface KeyMessage extends ResultOf<typeof KeyMessageItemFragment> {
  isCountLoading?: boolean;
}

export interface KeyMessageGroup
  extends ResultOf<typeof KeyMessageGroupFragment> {
  keyMessageItems: KeyMessage[];
}

// Modal State Slice
type ModalStateSlice = {
  editingKeyMessageId: string | null;
  insightsKeyMessageId: string | null;
  searchTerm: string;
  isCreateKeyMessageGroupModalOpen: boolean;
  isCreateKeyMessageModalOpen: boolean;
  isDeleteKeyMessageOrGroupModalOpen: boolean;
  isSaveSearchKeyMessageModalOpen: boolean;
  isKeyMessageInsightsModalOpen: boolean;
  keyMessageGroupName: string;
  keyMessageName: string;
  selectedGroup: {
    id: string;
    name: string;
  } | null;
  setCreateKeyMessageGroupModalOpen: (isOpen: boolean) => void;
  setSaveSearchKeyMessageModalOpen: (isOpen: boolean) => void;
  setCreateKeyMessageModalOpen: (isOpen: boolean) => void;
  setKeyMessageName: (name: string) => void;
  setSearchTerm: (term: string) => void;
  setIsAdvancedSearch: (isAdvancedSearch: boolean) => void;
  setSelectedGroup: (group: { name: string; id: string } | null) => void;
  setIsEditingKeyMessageGroup: (isEditing: boolean) => void;
  setKeyMessageGroupName: (name: string) => void;
  setIsEditingKeyMessage: (isEditing: boolean) => void;
  setEditingKeyMessageId: (id: string | null) => void;
  setInsightsKeyMessageId: (id: string | null) => void;
  setDeleteKeyMessageOrGroupModalOpen: (isOpen: boolean) => void;
  setIsDeletingKeyMessageAndGroup: (type: "group" | "message" | null) => void;
  setKeyMessageInsightsModalOpen: (isOpen: boolean) => void;
  resetSaveSearchKeyMessageModal: () => void;
  resetCreateKeyMessageModal: () => void;
  resetDeleteKeyMessageOrGroupModal: () => void;
};

// Filter State Slice
type FilterStateSlice = {
  selectedGroups: Set<string>;
  selectedKeyMessageFilterExclusion: FilterExclusion;
  setSelectedGroups: (groups: Set<string>) => void;
  updateSelectedKeyMessageFilterExclusion: (
    selectedKeyMessageFilterExclusion: FilterExclusion,
  ) => void;
};

// Creation State Slice
type CreateAndUpdateStateSlice = {
  createKeyMessageGroupState: "initial" | "complete";
  createKeyMessageState: "initial" | "complete";
  isCreatingKeyMessage: boolean;
  isCreatingKeyMessageGroup: boolean;
  isDeletingKeyMessageAndGroup: "group" | "message" | null;
  isDeletingKeyMessage: boolean;
  isDeletingKeyMessageGroup: boolean;
  isEditingKeyMessage: boolean;
  isEditingKeyMessageGroup: boolean;
  isMergingKeyMessageGroup: boolean;
};

// Data Fetch Slice
type DataFetchSlice = {
  keyMessageGroups: KeyMessageGroup[];
  keyMessages: KeyMessage[];
  isFetchingKeyMessageGroups: boolean;
  isFetchingKeyMessages: boolean;
  isFetchingKeyMessagesAndGroups: boolean;
};

// Search Slice
type SearchSlice = {
  isAdvancedSearch: boolean;
};

export type KeyMessageSlice = ModalStateSlice &
  CreateAndUpdateStateSlice &
  DataFetchSlice &
  FilterStateSlice &
  SearchSlice;

const modalInitialState = {
  editingKeyMessageId: null,
  insightsKeyMessageId: null,
  searchTerm: "",
  isCreateKeyMessageGroupModalOpen: false,
  isCreateKeyMessageModalOpen: false,
  isDeleteKeyMessageOrGroupModalOpen: false,
  isSaveSearchKeyMessageModalOpen: false,
  isKeyMessageInsightsModalOpen: false,
  keyMessageGroupName: "",
  keyMessageName: "",
  selectedGroup: null,
};

const filterInitialState = {
  selectedGroups: new Set<string>(),
  selectedKeyMessageFilterExclusion: "OR" as FilterExclusion,
};

const createAndUpdateInitialState = {
  createKeyMessageGroupState: "initial" as const,
  createKeyMessageState: "initial" as const,
  isCreatingKeyMessage: false,
  isCreatingKeyMessageGroup: false,
  isDeletingKeyMessageAndGroup: null as "group" | "message" | null,
  isDeletingKeyMessage: false,
  isDeletingKeyMessageGroup: false,
  isEditingKeyMessage: false,
  isEditingKeyMessageGroup: false,
  isMergingKeyMessageGroup: false,
  isFetchingKeyMessagesAndGroups: false,
};

const dataFetchInitialState = {
  keyMessageGroups: [] as KeyMessageGroup[],
  keyMessages: [] as KeyMessage[],
  isFetchingKeyMessageGroups: false,
  isFetchingKeyMessages: false,
};

const searchInitialState = {
  isAdvancedSearch: false,
};

const createKeyMessageSlice: StateCreator<
  KeyMessageSlice,
  [["zustand/devtools", never]]
> = (set, get, api) => ({
  ...modalInitialState,
  ...filterInitialState,
  ...createAndUpdateInitialState,
  ...dataFetchInitialState,
  ...searchInitialState,
  ...createKeyMessageActions(set, get, api),
});

// key message store
const useKeyMessageStore = create<KeyMessageSlice>()(
  devtools(createKeyMessageSlice, {
    name: "news-feed-key-messages",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useKeyMessageStore;
